import { Drawer, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useEffect, useState } from "react";

import { useLazyGetLiveUnitDefaultStreamCameraIdQuery } from "@/api/liveUnits/defaultCamera";
import { archiveCabinetInfoService } from "@/api/services/implementations";
import { liveUnitMetadataService } from "@/api/services/implementations/liveUnitMetadata";
import { useStyles } from "@/common/components/MapLandingPage/RightUnitDrawer/RightUnitDrawerStyles";
import { useDispatch, useSelector } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import { mapSelectors, setSelectedUnit } from "@/store/map/slice";
import { ArchiveCabinetInfo, isArchiveCabinetInfo } from "@/utilities/types/archive";
import { CameraInformation, LiveUnitMetaLanding } from "@/utilities/types/liveunit";

import { ArchiveCabinetInfoBox } from "./ArchiveCabinetInfoBox";
import CameraNavigation from "./CameraNavigation";
import DrawerNavigationButtons from "./DrawerNavigationButtons";
import { LiveUnitInfoBox } from "./LiveUnitInfoBox";


interface RightUnitDrawerProps {
    handleClose: () => void;
    isDrawerOpen: boolean;
}

const RightUnitDrawer: React.FC<RightUnitDrawerProps> = ({ isDrawerOpen, handleClose }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [getStreamCameraForLiveUnit, { data: defaultStreamCameraId }] = useLazyGetLiveUnitDefaultStreamCameraIdQuery();
    const selectedUnitId = useSelector(mapSelectors.getSelectedUnitId);
    const isArchiveCabinet = useSelector(mapSelectors.getIsArchiveCabinet);
    const isDeactivated = useSelector(mapSelectors.getIsDeactivated);
    const [liveUnitInfo, setLiveUnitInfo] = useState<LiveUnitMetaLanding | null>(null);
    const [cameraList, setCameraList] = useState<CameraInformation[] | null>(null);
    const [archiveCabinetInfo, setArchiveCabinetInfo] = useState<ArchiveCabinetInfo | null>(null);

    const hasCameras = cameraList && cameraList.length > 0 ? true : false;
    const defaultCameraId = defaultStreamCameraId ?? null;

    useEffect(() => {
        if (selectedUnitId && !isArchiveCabinet && !isDeactivated) {
            getStreamCameraForLiveUnit(selectedUnitId);
        }
    }, [selectedUnitId]);

    useEffect(() => {
        setLiveUnitInfo(null);
        setArchiveCabinetInfo(null);
        setCameraList(null);

        if (selectedUnitId) {
            if (isArchiveCabinet) {
                archiveCabinetInfoService
                    .fetchArchiveCabinetInfo(selectedUnitId)
                    .then((response) => {
                        if (isArchiveCabinetInfo(response)) {
                            setArchiveCabinetInfo(response);
                        }
                    })
                    .catch(() => {
                        setSelectedUnit(null);
                        dispatch(
                            addAlert({
                                severity: Severity.Error,
                                message: "Failed to get Archive Cabinet information.",
                            }),
                        );
                    });
            } else {
                liveUnitMetadataService
                    .fetchLiveUnitMetadataLanding(selectedUnitId)
                    .then((res) => {
                        setLiveUnitInfo(res);
                        setCameraList(res.cameraList);
                    })
                    .catch(() => {
                        setSelectedUnit(null);
                        dispatch(
                            addAlert({
                                severity: Severity.Error,
                                message: "Failed to get Live Unit information.",
                            }),
                        );
                    });
            }
        }
    }, [selectedUnitId, isArchiveCabinet]);

    return (
        <div data-testid="liveunit-drawer-wrapper">
            <Drawer anchor={"right"} open={isDrawerOpen} variant="persistent">
                <div className={classes.drawer}>
                    <div data-testid="close-button-wrapper">
                        <IconButton onClick={handleClose}>
                            <CloseIcon style={{ margin: 4 }} />
                        </IconButton>
                    </div>
                    <div className={classes.drawerComponents}>
                        {liveUnitInfo &&
                            (isDeactivated ? (
                                <>
                                    <LiveUnitInfoBox {...liveUnitInfo} />
                                    <DrawerNavigationButtons
                                        defaultCameraId={defaultCameraId}
                                        hasCameras={null}
                                        accessHistory={liveUnitInfo.accessArchives}
                                        accessStream={null}
                                    />
                                </>
                            ) : (
                                <>
                                    <LiveUnitInfoBox {...liveUnitInfo} />
                                    <DrawerNavigationButtons
                                        defaultCameraId={defaultCameraId}
                                        hasCameras={hasCameras}
                                        accessHistory={liveUnitInfo.accessArchives}
                                        accessStream={liveUnitInfo.accessStream}
                                    />
                                    <CameraNavigation defaultCameraId={defaultCameraId} cameraList={cameraList} />
                                </>
                            ))}
                        {archiveCabinetInfo && (
                            <>
                                <ArchiveCabinetInfoBox {...archiveCabinetInfo} />
                                <DrawerNavigationButtons
                                    defaultCameraId={defaultCameraId}
                                    hasCameras={null}
                                    accessHistory={archiveCabinetInfo.accessArchives}
                                    accessStream={null}
                                />
                            </>
                        )}
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default RightUnitDrawer;
